import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import PdfPreview from '../../../../Common/PDFPreview';
import { RingLoader } from 'react-spinners';
import PrintButton from "../../../../Common/Buttons/PrintPDF";
import { formatReadableAmount } from "../../../../Common/FormatFunctions/FormatAmount";
import { Typography, Button } from '@mui/material';
import {
    Paper,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell
} from '@mui/material';
import SearchBar from "../../../../Common/UtilityCommon/SearchBar";

const apikey = process.env.REACT_APP_API;

const PendingReports = () => {
    const Company_Name = sessionStorage.getItem("Company_Name");
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO');

    const navigate = useNavigate();
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pdfPreview, setPdfPreview] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const API_URL = `${apikey}/partywise-stockReport`;

    useEffect(() => {
        setLoading(true);
        axios.get(API_URL, {
            params: {
                Company_Code: sessionStorage.getItem('Company_Code'),
            }
        }).then(response => {
            setReportData(response.data);
            setLoading(false);
        }).catch(error => {
            console.error('Error fetching report:', error);
            setError('Failed to load data');
            setLoading(false);
        });
    }, []);

    const groupReportData = (data) => {
        const grouped = data.reduce((acc, item) => {
            if (parseFloat(item.BALANCE) !== 0) {
                const { buyername } = item;
                if (!acc[buyername]) {
                    acc[buyername] = {
                        items: [],
                        totalDespatch: 0,
                        totalBalance: 0,
                        totalQuantal: 0
                    };
                }
                acc[buyername].items.push(item);
                acc[buyername].totalDespatch += parseFloat(item.DESPATCH || 0);
                acc[buyername].totalBalance += parseFloat(item.BALANCE || 0);
                acc[buyername].totalQuantal += parseFloat(item.Buyer_Quantal || 0);
            }
            return acc;
        }, {});
        return grouped;
    };

    const groupedReportData = groupReportData(reportData);

    const calculateGrandTotals = () => {
        const totals = {
            totalQuantal: 0,
            totalDespatch: 0,
            totalBalance: 0,
        };
        Object.entries(groupedReportData).forEach(([_, group]) => {
            totals.totalQuantal += group.totalQuantal;
            totals.totalDespatch += group.totalDespatch;
            totals.totalBalance += group.totalBalance;
        });
        return totals;
    };

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        const excelRows = [];
        excelRows.push([
            "Tender No", "Mill Name", "Grade", "M Rate", "S Rate", "Lifting", "Do", "Quantal", "Desp", "Bal"
        ]);

        Object.entries(groupedReportData).forEach(([buyername, group]) => {
            excelRows.push([{ v: buyername, s: { font: { bold: true } }, t: 's' }]);
            group.items.forEach(item => {
                excelRows.push([
                    item.Tender_No,
                    item.millshortname || '',
                    item.Grade,
                    item.Mill_Rate,
                    item.Sale_Rate || '',
                    item.Tender_Date || '0',
                    item.Do || '',
                    item.Buyer_Quantal || '0',
                    item.DESPATCH || '0',
                    item.BALANCE || '0'
                ]);
            });

            excelRows.push([
                "Total", "", "", "", "", "", "", group.totalQuantal.toFixed(2),
                group.totalDespatch.toFixed(2),
                group.totalBalance.toFixed(2)
            ]);
        });

        const grandTotals = calculateGrandTotals();
        excelRows.push([
            "Grand Totals", "", "", "", "", "", "",
            grandTotals.totalQuantal.toFixed(2),
            grandTotals.totalDespatch.toFixed(2),
            grandTotals.totalBalance.toFixed(2)
        ]);

        const ws = XLSX.utils.aoa_to_sheet(excelRows);
        XLSX.utils.book_append_sheet(wb, ws, 'PartywiseBalaceStockReport');
        XLSX.writeFile(wb, 'PartywiseBalaceStockReport.xlsx');
    };

    const generatePDF = () => {
        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 255);
        const companyTextWidth = doc.getTextWidth(Company_Name);
        doc.text(Company_Name, (pageWidth - companyTextWidth) / 2, 10);
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        const titleText = "Partywise Sugar Balance Stock";
        doc.text(titleText, (pageWidth - doc.getTextWidth(titleText)) / 2, 18);
        doc.setFontSize(10);
        doc.text("Balance Stock", (pageWidth - doc.getTextWidth("Balance Stock")) / 2, 25);
        let yOffset = 30;

        Object.entries(groupedReportData).forEach(([buyername, group]) => {
            doc.setFont("helvetica", "bold");
            doc.setFillColor(230, 230, 230);
            doc.rect(10, yOffset, 190, 8, "F");
            doc.text(`Buyer: ${buyername}`, 12, yOffset + 5);
            yOffset += 10;

            const tableBody = group.items.map((item) => [
                item.Tender_No,
                item.millshortname || "",
                item.Grade,
                item.Mill_Rate,
                item.Sale_Rate || 0,
                item.Tender_Date || 0,
                item.Do || "",
                item.Buyer_Quantal || 0,
                item.DESPATCH || 0,
                item.BALANCE || 0
            ]);

            tableBody.push([
                "Total", "", "", "", "", "", "",
                group.totalQuantal.toFixed(2),
                group.totalDespatch.toFixed(2),
                group.totalBalance.toFixed(2),
            ]);

            doc.autoTable({
                startY: yOffset,
                head: [[
                    "Tender No", "Mill Name", "Grade", "Mill Rate", "Sale Rate",
                    "Lifting", "Do", "Quintal", "Desp", "Balance"
                ]],
                body: tableBody,
                theme: "grid",
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
                // alternateRowStyles: { fillColor: [240, 240, 240] },
                margin: { top: 10, left: 10, right: 10 },
            });

            yOffset = doc.lastAutoTable.finalY + 10;
            if (yOffset > 270) {
                doc.addPage();
                yOffset = 10;
            }
        });

        const grandTotals = calculateGrandTotals();
        doc.setFontSize(10);
        doc.text(
            `Grand Totals: Quintal = ${grandTotals.totalQuantal.toFixed(2)}, Despatch = ${grandTotals.totalDespatch.toFixed(2)}, Balance = ${grandTotals.totalBalance.toFixed(2)}`,
            10,
            yOffset
        );

        const pdfBlob = doc.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfPreview(pdfUrl);
    };

    return (
        <div style={{ marginTop: "-90px" }}>
            {/* <Typography variant="h6" style={{ textAlign: 'center', fontSize: "18px", fontWeight: "bold" }}>{Company_Name}</Typography> */}
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", fontWeight: "bold", color: "blue" }}>
                Partywise Sugar Balance Stock
            </Typography>

            <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "10px" }}>
                <div style={{ flex: 1, minWidth: "250px", maxWidth: "1100px" }}>
                    <SearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>

                <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
                    {pdfPreview && <PdfPreview pdfData={pdfPreview} apiData={reportData} label={"Partywise Balance Stock Report"} />}
                    <PrintButton disabledFeild={""} fetchData={generatePDF} />
                    <Button variant="outlined" color="secondary" onClick={handleExportToExcel}>Export to Excel</Button>
                    <Button variant="outlined" color="secondary" onClick={() => window.open('/millwise-stock', '_blank')}>
                        Mill Wise Lifting Wise
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => window.open('/self-stock', '_blank')}>
                        Self Stock
                    </Button>
                </div>
            </div>

            <div className="table-responsive">
                <Paper elevation={3} sx={{ marginBottom: 1 }}>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={{
                                    height: 30,
                                    '& td, & th': {
                                        padding: '4px 8px',
                                    }
                                }}>
                                    <TableCell align='center' style={{ color: "black", fontWeight: "bold" }}>Tender No</TableCell>
                                    <TableCell style={{ color: "black", fontWeight: "bold" }}>Mill Name</TableCell>
                                    <TableCell style={{ color: "black", fontWeight: "bold" }}>Grade</TableCell>
                                    <TableCell align='right' style={{ color: "black", fontWeight: "bold" }}>Mill Rate</TableCell>
                                    <TableCell align='right' style={{ color: "black", fontWeight: "bold" }}>Sale Rate</TableCell>
                                    <TableCell style={{ color: "black", fontWeight: "bold" }}>Lifting Date</TableCell>
                                    <TableCell style={{ color: "black", fontWeight: "bold" }}>Sauda Date</TableCell>
                                    <TableCell style={{ color: "black", fontWeight: "bold" }}>Do</TableCell>
                                    <TableCell align='right' style={{ color: "black", fontWeight: "bold" }}>Quintal</TableCell>
                                    <TableCell align='right' style={{ color: "black", fontWeight: "bold" }}>Dispatch</TableCell>
                                    <TableCell align='right' style={{ color: "black", fontWeight: "bold" }}>Balance</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(groupedReportData)
                                    .filter(([buyername, group]) =>
                                        searchQuery.trim() === '' ||
                                        buyername.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                        group.items.some(item =>
                                            Object.values(item).some(val =>
                                                val &&
                                                val.toString().toLowerCase().includes(searchQuery.toLowerCase())
                                            )
                                        )
                                    )
                                    .map(([buyername, group]) => (
                                        <React.Fragment key={buyername}>
                                            <TableRow >
                                                <TableCell colSpan={10} sx={{ color: 'blue', fontWeight: 'bold' }}>{buyername}</TableCell>
                                            </TableRow>
                                            {group.items.map((item, index) => (
                                                <TableRow key={index} hover sx={{
                                                    height: 30,
                                                    '& td, & th': {
                                                        padding: '4px 8px',
                                                    }
                                                }}>
                                                    <TableCell>{item.Tender_No}</TableCell>
                                                    <TableCell>{item.millshortname || ''}</TableCell>
                                                    <TableCell>{item.Grade}</TableCell>
                                                    <TableCell align='right'>{formatReadableAmount(item.Mill_Rate)}</TableCell>
                                                    <TableCell align='right'>{formatReadableAmount(item.Sale_Rate)}</TableCell>
                                                    <TableCell>{item.Tender_Date}</TableCell>
                                                    <TableCell>{item.Sauda_Date}</TableCell>
                                                    <TableCell>{item.Do}</TableCell>
                                                    <TableCell align='right'>{formatReadableAmount(item.Buyer_Quantal)}</TableCell>
                                                    <TableCell align='right'>{item.DESPATCH}</TableCell>
                                                    <TableCell align='right'>{item.BALANCE}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{
                                                height: 30,
                                                '& td, & th': {
                                                    padding: '4px 8px',
                                                    backgroundColor: '#f3f388', fontWeight: 'bold'
                                                }
                                            }} >
                                                <TableCell colSpan={8} align='right'>Total</TableCell>
                                                <TableCell align='right'>{formatReadableAmount(group.totalQuantal.toFixed(2))}</TableCell>
                                                <TableCell align='right'>{formatReadableAmount(group.totalDespatch.toFixed(2))}</TableCell>
                                                <TableCell align='right'>{formatReadableAmount(group.totalBalance.toFixed(2))}</TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                <TableRow sx={{
                                    height: 30,
                                    '& td, & th': {
                                        padding: '4px 8px',
                                        backgroundColor: '#f3f388', fontWeight: 'bold'
                                    }
                                }}   >
                                    <TableCell colSpan={8} style={{ color: "black", fontWeight: "bold" }}>Grand Totals</TableCell>
                                    <TableCell align='right' style={{ color: "black", fontWeight: "bold" }}>{formatReadableAmount(calculateGrandTotals().totalQuantal.toFixed(2))}</TableCell>
                                    <TableCell align='right' style={{ color: "black", fontWeight: "bold" }}>{formatReadableAmount(calculateGrandTotals().totalDespatch.toFixed(2))}</TableCell>
                                    <TableCell align='right' style={{ color: "black", fontWeight: "bold" }}>{formatReadableAmount(calculateGrandTotals().totalBalance.toFixed(2))}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </div>
            {loading && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <RingLoader />
                </div>
            )}
            {error && <p style={{ color: 'red' }}>{error}</p>}
        </div>
    );
};

export default PendingReports;

